import { FC } from 'react';
import Link from 'next/link';
import { parseISO, format } from 'date-fns';
import { getLanguage } from '@/helpers/helpers';
import { getAuthorFullName } from '@/helpers/helpers';
import RatingStars from './rating-stars/RatingStars';
import RelativeBook from '../relative-book/RelativeBook';
import LitresWidget from '@/components/ui/litres-widget/LitresWidget';
import LitresAudioPlayer from '../litres-audio-player/LitresAudioPlayer';
import BookSeries from './book-series/BookSeries';
import BookRatingJsonMarkup from '../book-rating-json-markup/BookRatingJsonMarkup';
import SocialsBar from '../socials-bar/SocialsBar';
import VersionSticker from '../version-sticker/VersionSticker';
import { DEV_ABSOLUTE_URL } from '@/helpers/variables';
import { IBookShortDetail } from '@/interfaces/bookShortDetail.interface';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { hideImgOnError } from '@/helpers/helpers';
import styles from './BookInfo.module.scss';

interface IBookInfo {
  book: IBookShortDetail;
  reviewsCount: number;
  formRef?: React.RefObject<HTMLDivElement>;
  readPage?: boolean;
}

const BookInfo: FC<IBookInfo> = ({ book, reviewsCount, formRef, readPage }) => {
  const { rating } = book;
  const { type, title, lang, year, external_id } = book.book;
  const { first_name, middle_name, last_name } = book.author;

  const currentPath = String(useGetUrlPath());
  const isReadPage = currentPath.includes('/online/');

  const authorFullName = getAuthorFullName(first_name, middle_name, last_name);
  const authorFullAlias = `/library/author/${book.author.id}`;
  const createdAt = book.created_at;

  return (
    <>
      {!readPage && rating > 0 && <BookRatingJsonMarkup book={book} authorFullName={authorFullName} />}

      <div className={styles.bookInfo}>
        <div className={styles.bookInfo__imageWrap}>
          <div className={`${styles.bookInfo__title} ${styles.bookInfo__title_mob}`} data-widget-litres-book>
            {title}
          </div>

          <div className={styles.bookInfo__cover} style={{ opacity: 0 }}>
            <img className={styles.bookInfo__image} width="300" height="425" src={`${DEV_ABSOLUTE_URL}/covers_330/${external_id}.jpg`} alt={title} onError={hideImgOnError} />
            {book.book.allow_full_free > 0 && book.type !== 'pdf' && <VersionSticker />}
          </div>

          {!book.hide_litres_data && (
            <div className={styles.litresWidget}>
              <h3 className={styles.litresWidget__title}>Полная версия</h3>
              <div>
                <LitresWidget bookTitle={title} authorName={authorFullName} />
              </div>
            </div>
          )}

          {type === 'audio_book' ? (
            <div className={styles.bookInfo__audioPlayer} id="audioPlayer">
              <h3 className={styles.litresWidget__title}>Слушать онлайн</h3>
              <LitresAudioPlayer externalId={external_id} />
            </div>
          ) : (
            ''
          )}
        </div>

        <div className={styles.bookInfo__desc}>
          <h1 className={styles.bookInfo__title} data-widget-litres-book>
            {title}
          </h1>

          <div className={styles.bookInfo__details}>
            <div className={`${styles.bookInfo__row} ${styles.bookInfo__row_type}`}>
              {!!book.relative_book?.id ? (
                <RelativeBook alias={`/book/${book.relative_book.id}`} currentBookType={book.book.type} />
              ) : (
                <>
                  {type === 'audio_book' ? (
                    <a href="#audioPlayer" className={`${styles.bookInfo__bookType} ${styles.bookInfo__bookType_audio}`}>
                      aудио
                    </a>
                  ) : (
                    <>
                      {isReadPage ? (
                        <a href="#readBlock" className={styles.bookInfo__bookType}>
                          текст
                        </a>
                      ) : (
                        <Link prefetch={false} href={`/online/${book.id}`} className={styles.bookInfo__bookType}>
                          текст
                        </Link>
                      )}
                    </>
                  )}
                </>
              )}
              <RatingStars rating={rating} title={title} id={book.id} formRef={formRef} />
              {!isReadPage && <SocialsBar bookLink={`/book/${book.id}`} />}

              <div className={styles.bookInfo__reviews}>{reviewsCount > 0 ? reviewsCount : 0}</div>
            </div>

            <div className={`${styles.bookInfo__row} ${styles.bookInfo__row_info}`}>
              <div className={styles.bookInfo__author}>
                <Link prefetch={false} href={authorFullAlias} data-widget-litres-author>
                  {authorFullName}
                </Link>
              </div>

              <div className={styles.bookInfo__categoryName}>
                <span className={styles.bookInfo__categoryName}>Жанр: </span>
                {book.genres.map((genre) => (
                  <Link prefetch={false} key={genre.id} className={styles.bookInfo__categoryLink} href={`/library/dir/russian/genre/${genre.alias}`}>
                    {genre.title}
                  </Link>
                ))}
              </div>

              {book.reader && (
                <div className={styles.bookInfo__categoryName}>
                  <span className={styles.bookInfo__categoryName}>Чтец: </span>
                  <Link prefetch={false} href={`/library/reader/${book.reader.alias}/`} className={styles.bookInfo__categoryLink}>
                    {getAuthorFullName(book.reader.first_name, book.reader.middle_name, book.reader.last_name)}
                  </Link>
                </div>
              )}

              <div className={styles.bookInfo__categoryName}>
                <span className={styles.bookInfo__categoryName}>Язык: </span>
                <span className={styles.bookInfo__categoryTxt}>{getLanguage(lang)}</span>
              </div>

              {!!year && (
                <div className={styles.bookInfo__categoryName}>
                  <span className={styles.bookInfo__categoryName}>Год издания: </span>
                  <span className={styles.bookInfo__categoryTxt}>{year}</span>
                </div>
              )}

              <div className={styles.bookInfo__categoryName}>
                <span className={styles.bookInfo__categoryName}>Добавлена: </span>
                <span className={styles.bookInfo__categoryTxt}>
                  <time dateTime={createdAt}>{format(parseISO(createdAt), 'dd.MM.yy')}</time>
                </span>
              </div>

              {book.series && book.series[0]?.other_series_books.length > 0 && <BookSeries series={book.series} />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookInfo;
